import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import {
  journalAppointmentChoice,
  journalAppointmentQuestion,
  type JournalDayAppointment,
  type JournalLinkPrompt,
} from '@/lib/journalSameDayLink';

export function JournalAppointmentLinkPrompt({
  prompt,
  onLink,
  onDecline,
  onDismiss,
}: {
  prompt: Exclude<JournalLinkPrompt, { kind: 'skip' }>;
  onLink: (appointmentId: string) => void;
  onDecline: () => void;
  onDismiss: () => void;
}) {
  const { t } = useTranslation();
  const title = t('journal.link_which', 'Hvilken aftale er notatet til?');

  if (prompt.kind === 'one') {
    const when = journalAppointmentQuestion(prompt.appointment);
    return (
      <Modal
        title={title}
        description={t('journal.link_one_question', {
          when,
          defaultValue: `Skal notatet knyttes til aftalen ${when}?`,
        })}
        onClose={onDismiss}
        footer={
          <>
            <Button type="button" variant="secondary" onClick={onDecline}>
              {t('common.no', 'Nej')}
            </Button>
            <Button type="button" onClick={() => onLink(prompt.appointment.id)}>
              {t('common.yes', 'Ja')}
            </Button>
          </>
        }
      >
        <span className="sr-only" data-testid="journal-link-one">
          {when}
        </span>
      </Modal>
    );
  }

  return (
    <Modal title={title} onClose={onDismiss}>
      <div className="flex flex-col gap-2" data-testid="journal-link-many">
        {prompt.appointments.map((appointment) => (
          <AppointmentChoice
            key={appointment.id}
            appointment={appointment}
            onLink={onLink}
          />
        ))}
        <Button type="button" variant="secondary" onClick={onDecline}>
          {t('journal.link_none', 'Nej, ikke knyttet til en aftale')}
        </Button>
      </div>
    </Modal>
  );
}

function AppointmentChoice({
  appointment,
  onLink,
}: {
  appointment: JournalDayAppointment;
  onLink: (appointmentId: string) => void;
}) {
  return (
    <Button type="button" variant="secondary" onClick={() => onLink(appointment.id)}>
      {journalAppointmentChoice(appointment)}
    </Button>
  );
}
